import { useEffect, useState } from "react";
import { Button, Modal, TextField, Tooltip } from "@mui/material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "../../api/axios";

const STORAGE_KEY = "suggestion_form_hidden";
const MAX_LENGTH = 1000;
export const SUGGESTION_FORM_ALLOWED_USER_IDS = [4000000003];

const SuggestionForm = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const currentUserId = JSON.parse(localStorage.getItem("user") || "null")?.user?.id;
  const isAllowed = SUGGESTION_FORM_ALLOWED_USER_IDS.includes(Number(currentUserId));

  useEffect(() => {
    if (!isAllowed) return;
    const hidden = localStorage.getItem(STORAGE_KEY) === "true";
    if (!hidden) setOpen(true);
  }, [isAllowed]);

  if (!isAllowed) return null;

  const handleClose = () => {
    setMessage("");
    setOpen(false);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setMessage("");
    setOpen(false);
  };

  const handleSubmit = async () => {
    const trimmed = message.trim();
    if (!trimmed) {
      toast.warning("Lütfen önerinizi yazınız.");
      return;
    }
    setLoading(true);
    try {
      await axios.post("/member/system/suggestion", { message: trimmed });
      toast.success("Öneriniz için teşekkür ederiz! Geri bildiriminiz bizim için çok değerli.");
      setMessage("");
      localStorage.setItem(STORAGE_KEY, "true");
      setOpen(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Öneriniz gönderilirken bir hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  const charCount = message.length;
  const remaining = MAX_LENGTH - charCount;

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        style={{ zIndex: 10000 }}
      />
      <Modal
        open={open}
        onClose={handleClose}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          zIndex: 9999,
          outline: "none",
        }}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "var(--lighterBg)",
            maxWidth: "560px",
            width: "100%",
            overflow: "hidden",
            borderRadius: "16px",
            outline: "none",
            maxHeight: "90vh",
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          }}
        >
          {/* Header */}
          <div
            style={{
              position: "relative",
              padding: "28px 28px 20px",
              background:
                "linear-gradient(135deg, var(--btnBgBlue) 0%, #4a8fd1 100%)",
              color: "#fff",
            }}
          >
            <button
              onClick={handleClose}
              aria-label="Kapat"
              style={{
                position: "absolute",
                top: "14px",
                right: "14px",
                background: "rgba(255,255,255,0.18)",
                border: "none",
                cursor: "pointer",
                padding: "6px",
                display: "flex",
                borderRadius: "50%",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.32)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.18)")
              }
            >
              <CloseIcon style={{ color: "#fff", fontSize: "20px" }} />
            </button>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                marginBottom: "10px",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  backgroundColor: "rgba(255,255,255,0.22)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <LightbulbOutlinedIcon style={{ color: "#fff", fontSize: "28px" }} />
              </div>
              <div>
                <h2
                  style={{
                    fontSize: "20px",
                    lineHeight: "24px",
                    color: "#fff",
                    margin: 0,
                    fontWeight: 600,
                  }}
                >
                  Görüşleriniz Bizim İçin Değerli
                </h2>
                <p
                  style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.85)",
                    margin: "4px 0 0",
                  }}
                >
                  Birlikte daha iyi bir deneyim oluşturalım
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div
            style={{
              padding: "22px 28px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              overflow: "auto",
            }}
          >
            <p
              style={{
                fontSize: "14px",
                lineHeight: "22px",
                color: "var(--text)",
                margin: 0,
              }}
            >
              iyziShip'i kullanırken karşılaştığınız zorlukları, eklenmesini
              istediğiniz özellikleri veya geliştirme önerilerinizi bizimle
              paylaşın. Her geri bildirim, ürünümüzü sizin için daha iyi hale
              getirmemize yardımcı olur.
            </p>

            <div>
              <TextField
                multiline
                minRows={5}
                maxRows={8}
                value={message}
                onChange={(e) => {
                  if (e.target.value.length <= MAX_LENGTH) {
                    setMessage(e.target.value);
                  }
                }}
                fullWidth
                disabled={loading}
                placeholder="Örn: Sipariş takip ekranında filtreleme seçeneklerinin artması faydalı olur..."
                inputProps={{ maxLength: MAX_LENGTH }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "var(--background, #fafafa)",
                    fontSize: "14px",
                  },
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  fontSize: "12px",
                  color: remaining < 50 ? "#d32f2f" : "var(--priceText)",
                  marginTop: "6px",
                }}
              >
                {charCount} / {MAX_LENGTH}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "16px 28px 22px",
              borderTop: "1px solid rgba(0,0,0,0.06)",
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "var(--background, #fafafa)",
            }}
          >
            <Button
              onClick={handleDontShowAgain}
              disabled={loading}
              startIcon={<VisibilityOffOutlinedIcon />}
              sx={{
                color: "var(--priceText)",
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 500,
                padding: "8px 12px",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
              }}
            >
              Bir daha gösterme
            </Button>

            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                onClick={handleClose}
                disabled={loading}
                sx={{
                  color: "var(--text)",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: "10px 20px",
                  borderRadius: "10px",
                  border: "1px solid rgba(0,0,0,0.12)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                    border: "1px solid rgba(0,0,0,0.2)",
                  },
                }}
              >
                Kapat
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !message.trim()}
                endIcon={!loading && <SendRoundedIcon />}
                sx={{
                  backgroundColor: "var(--btnBgBlue)",
                  color: "#fff",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  padding: "10px 22px",
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(74, 143, 209, 0.35)",
                  "&:hover": {
                    backgroundColor: "var(--btnBgBlue)",
                    opacity: 0.92,
                    boxShadow: "0 6px 16px rgba(74, 143, 209, 0.45)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(0,0,0,0.12)",
                    color: "rgba(0,0,0,0.4)",
                    boxShadow: "none",
                  },
                }}
              >
                {loading ? "Gönderiliyor..." : "Öneriyi Gönder"}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {!open && (
        <Tooltip title="Öneri & Tavsiye gönder" placement="left" arrow>
          <button
            onClick={() => setOpen(true)}
            aria-label="Öneri formunu aç"
            style={{
              position: "fixed",
              right: "24px",
              bottom: "24px",
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              border: "none",
              background:
                "linear-gradient(135deg, var(--btnBgBlue) 0%, #4a8fd1 100%)",
              color: "#fff",
              cursor: "pointer",
              boxShadow: "0 8px 24px rgba(74, 143, 209, 0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9998,
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.08)";
              e.currentTarget.style.boxShadow =
                "0 12px 28px rgba(74, 143, 209, 0.55)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow =
                "0 8px 24px rgba(74, 143, 209, 0.45)";
            }}
          >
            <LightbulbOutlinedIcon style={{ color: "#fff", fontSize: "26px" }} />
          </button>
        </Tooltip>
      )}
    </>
  );
};

export default SuggestionForm;
